/** @jsxRuntime classic */
import { gql, useQuery } from '@apollo/client';
import useUserProfile from '@bottlebooks/gatsby-plugin-firebase-auth/src/useUserProfile';
import { Button, Flex, Link, Text } from '@bottlebooks/gatsby-theme-base/src';
import React from 'react';
import Dialog from '../Dialog';
import { useCollection } from '../SiteSearch/useEventData';
import UserProfileCard from '../UserProfile/UserProfileCard';
import useSiteConfig from '../useSiteConfig';
import { Trans } from '@lingui/macro';

export default function ClaimAccountNotificationBar({
  locale,
  ...rest
}: {
  locale: string;
}) {
  const { userProfile } = useUserProfile();
  const { isMeetingBookingEnabled } = useSiteConfig();
  if (!isMeetingBookingEnabled) return null;
  if (!userProfile) return null;
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.toLowerCase().includes('onboarding')
  )
    return null;
  return (
    <CanClaimRepresentativeProfile
      userProfile={userProfile}
      locale={locale}
      {...rest}
    />
  );
}

function CanClaimRepresentativeProfile({
  userProfile,
  locale,
  ...rest
}: {
  userProfile: any;
  locale: string;
}) {
  const { collectionId } = useCollection();
  const { data } = useQuery(query, {
    variables: {
      collectionId,
      userId: userProfile.uid,
    },
  });
  const canClaimRepresentative =
    data?.collection?.representatives?.filter(
      (rep) => rep.mayClaim && rep.claimedState === 'UNCLAIMED'
    ) || [];
  const canClaimVisitor =
    data?.collection?.visitors?.filter(
      (rep) => rep.mayClaim && rep.claimedState === 'UNCLAIMED'
    ) || [];

  const canClaim = canClaimRepresentative.length || canClaimVisitor.length;
  if (canClaim)
    return (
      <Dialog style={{ width: 400 }}>
        <Dialog.Header onDismiss={() => {}}>
          <Trans>Confirm your registration</Trans>
        </Dialog.Header>
        <Dialog.Content>
          <UserProfileCard.Horizontal
            profile={canClaimVisitor[0]}
            hideActions
            sx={{ padding: 0 }}
            {...rest}
          />
        </Dialog.Content>
        <Dialog.Footer>
          <Flex
            gap={4}
            align="center"
            justify="space-between"
            style={{ width: '100%' }}
          >
            <Link href="mailto:support@bottlebooks.me">
              <Text>
                <Trans>Contact support</Trans>
              </Text>
            </Link>
            <Button
              variant="primary"
              href={
                canClaimVisitor.length
                  ? `/${locale}/attendeeOnboarding`
                  : `/${locale}/representativeOnboarding`
              }
            >
              <Text>
                <Trans>Yes, this is me</Trans>
              </Text>
            </Button>
          </Flex>
        </Dialog.Footer>
      </Dialog>
    );
  return null;
}

const query = gql`
  query CanClaimRepresentativeProfile($collectionId: ID!, $userId: ID!) {
    collection(collectionId: $collectionId) {
      eventId: collectionId
      representatives(
        experiment: "california-networking"
        experimentToken: "Used in @bottlebooks/gatsby-theme-event, added by bernhard@unserwein.at at 2023-03-08"
      ) {
        collectionId
        mayClaim(userId: $userId)
        companyName
        registrationId
        representativeId
        claimedState
        ...UserProfileCard
      }
      visitors(
        experimentalKey: "jonathan@bottlebooks.me: @bottlebooks/gatsby-theme-event/src/components/UserProfile/UserProfilesPage.useFetcher.tsx"
        visibleToRoles: [REPRESENTATIVE, VISITOR, ANONYMOUS]
      ) {
        collectionId
        mayClaim(userId: $userId)
        companyName
        claimedState
        attendeeProfileIds
        ...UserProfileCard
      }
    }
  }
  ${UserProfileCard.fragment}
`;
